import React, { useCallback, useRef } from 'react';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import isEmpty from 'lodash/isEmpty';

const emptySelectionStore = {
  subscribe: (_blockId, _listener) => () => {},
  getItemSnapshot: () => 'false:false',
};

const getVerticalPlaceholder = (
  draggedDOM,
  sourceIndex,
  destinationIndex,
  parentDOM,
) => {
  const childrenArray = [...parentDOM.children];
  childrenArray.splice(sourceIndex, 1);
  childrenArray.splice(-1, 1);
  const parentRect = parentDOM.getBoundingClientRect();
  const prevNode = childrenArray[destinationIndex - 1];
  const nextNode = childrenArray[destinationIndex];
  let top;
  let bottom;

  if (prevNode) {
    const prevRect = prevNode.getBoundingClientRect();
    top = prevRect.top + prevRect.height - parentRect.top;
  } else {
    top = 0;
  }

  if (nextNode) {
    const nextRect = nextNode.getBoundingClientRect();
    bottom = nextRect.top - parentRect.top;
  } else {
    bottom =
      parentRect.bottom +
      draggedDOM.getBoundingClientRect().height -
      parentRect.top;
  }

  return {
    clientY: top,
    clientHeight: bottom - top,
    clientX: parseFloat(window.getComputedStyle(parentDOM).paddingLeft),
    clientWidth: draggedDOM.clientWidth,
  };
};

const getHorizontalPlaceholder = (draggedDOM, destinationIndex, parentDOM) => {
  const parentRect = parentDOM.getBoundingClientRect();
  const height = parentRect.bottom - parentRect.top;
  let clientWidth = draggedDOM.clientWidth;
  let marginLeft =
    parentDOM.children[destinationIndex].getBoundingClientRect().x;

  if (parentDOM.children.length - 1 === destinationIndex) {
    marginLeft = parentRect.x;
    clientWidth = parentDOM.getBoundingClientRect().width;
  }

  return {
    clientY: 0,
    clientHeight: height,
    clientX: parseFloat(marginLeft - parentRect.x),
    clientWidth,
  };
};

const DraggableItem = React.memo(function DraggableItem({
  Draggable,
  child,
  childId,
  index,
  children,
  selectionStore,
}) {
  const store = selectionStore || emptySelectionStore;
  const subscribe = React.useCallback(
    (listener) => store.subscribe(childId, listener),
    [childId, store],
  );
  const selection = React.useSyncExternalStore(
    subscribe,
    () => store.getItemSnapshot(childId),
    () => 'false:false',
  );
  const [selected, multiSelected] = selection
    .split(':')
    .map((value) => value === 'true');

  return (
    <Draggable
      draggableId={childId.toString()}
      index={index}
      style={{ userSelect: 'none' }}
    >
      {(draginfo) =>
        children({
          child,
          childId,
          index,
          draginfo,
          selected,
          multiSelected,
        })
      }
    </Draggable>
  );
});

const DragDropList = (props) => {
  const {
    childList,
    children,
    direction = 'vertical',
    onMoveItem,
    as = 'div',
    style,
    forwardedAriaLabelledBy,
    reactBeautifulDnd,
    selectedBlock,
    multiSelected,
    selectionStore,
    renderVersion,
  } = props;
  const { DragDropContext, Draggable, Droppable } = reactBeautifulDnd;
  const [placeholderProps, setPlaceholderProps] = React.useState({});
  const uid = React.useId();
  const timer = useRef(null);
  const parentRef = useRef(null);

  const onDragStart = useCallback(
    (event) => {
      clearTimeout(timer.current);
      const draggedDOM = document.querySelector(
        `[data-rbd-draggable-id='${event.draggableId}']`,
      );
      if (!draggedDOM) return;

      const sourceIndex = event.source.index;
      setPlaceholderProps(
        direction === 'horizontal'
          ? getHorizontalPlaceholder(draggedDOM, sourceIndex, parentRef.current)
          : getVerticalPlaceholder(
              draggedDOM,
              sourceIndex,
              sourceIndex,
              parentRef.current,
            ),
      );
    },
    [direction],
  );

  const onDragEnd = useCallback(
    (result) => {
      clearTimeout(timer.current);
      onMoveItem(result);
      setPlaceholderProps({});
    },
    [onMoveItem],
  );

  const onDragUpdate = useCallback(
    (update) => {
      clearTimeout(timer.current);
      setPlaceholderProps({});
      if (!update.destination) return;

      const draggedDOM = document.querySelector(
        `[data-rbd-draggable-id='${update.draggableId}']`,
      );
      if (!draggedDOM) return;

      const sourceIndex = update.source.index;
      const destinationIndex = update.destination.index;
      timer.current = setTimeout(() => {
        setPlaceholderProps(
          direction === 'horizontal'
            ? getHorizontalPlaceholder(
                draggedDOM,
                destinationIndex,
                parentRef.current,
              )
            : getVerticalPlaceholder(
                draggedDOM,
                sourceIndex,
                destinationIndex,
                parentRef.current,
              ),
        );
      }, 250);
    },
    [direction],
  );

  const AsDomComponent = as;

  return (
    <DragDropContext
      onDragStart={onDragStart}
      onDragUpdate={onDragUpdate}
      onDragEnd={onDragEnd}
    >
      <Droppable
        droppableId={uid}
        direction={direction}
        renderClone={(provided, _snapshot, rubric) => {
          const index = rubric.source.index;
          return children({
            child: childList[index][1],
            childId: childList[index][0],
            index,
            draginfo: provided,
          });
        }}
      >
        {(provided, snapshot) => (
          <AsDomComponent
            ref={(element) => {
              provided.innerRef(element);
              parentRef.current = element;
            }}
            {...provided.droppableProps}
            style={{ ...style, position: 'relative' }}
            aria-labelledby={forwardedAriaLabelledBy}
          >
            {childList
              .filter(([id, child]) => id && child)
              .map(([childId, child], index) => (
                <DraggableItem
                  key={childId}
                  Draggable={Draggable}
                  child={child}
                  childId={childId}
                  index={index}
                  children={children}
                  selectionStore={selectionStore}
                  selected={selectedBlock === childId}
                  multiSelected={multiSelected?.includes(childId)}
                  renderVersion={renderVersion}
                />
              ))}
            {provided.placeholder}
            {!isEmpty(placeholderProps) && snapshot.isDraggingOver && (
              <div
                style={{
                  position: 'absolute',
                  top: placeholderProps.clientY,
                  left: placeholderProps.clientX,
                  height: placeholderProps.clientHeight,
                  background: '#eee',
                  width: placeholderProps.clientWidth,
                  borderRadius: '3px',
                  ...(direction === 'horizontal' && { zIndex: -1 }),
                }}
              />
            )}
          </AsDomComponent>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default injectLazyLibs(['reactBeautifulDnd'])(DragDropList);
